import { useCallback, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Tracks a CSS media query (for layout decisions CSS alone cannot make, e.g. the shell rail switching between
 * icon-only and labelled at 1280px). `useSyncExternalStore` keeps every subscriber consistent within a render.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', notify);
      return () => mql.removeEventListener('change', notify);
    },
    [query],
  );
  const read = useCallback(
    () => (typeof window !== 'undefined' && !!window.matchMedia ? window.matchMedia(query).matches : false),
    [query],
  );
  return useSyncExternalStore(subscribe, read, () => false);
}

/** The latest value in a ref, for event handlers registered once (listeners, timers) that must see fresh props. */
export function useLatest<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Calls `onOutside` for a pointerdown outside every given element (menus, popovers). Registered in the capture phase
 * so a click that another component stops still closes the menu.
 */
export function useOutsidePointerDown(
  active: boolean,
  refs: ReadonlyArray<{ readonly current: HTMLElement | null }>,
  onOutside: (event: PointerEvent) => void,
): void {
  const handler = useLatest(onOutside);
  const targets = useLatest(refs);
  useEffect(() => {
    if (!active) return;
    const listener = (event: PointerEvent) => {
      const node = event.target as Node | null;
      if (!node) return;
      if (targets.current.some((r) => r.current?.contains(node))) return;
      handler.current(event);
    };
    document.addEventListener('pointerdown', listener, true);
    return () => document.removeEventListener('pointerdown', listener, true);
  }, [active, handler, targets]);
}

/** Re-renders every `intervalMs` (relative times such as "2 min ago"); returns a changing tick. */
export function useTicker(intervalMs: number): number {
  const subscribe = useCallback(
    (notify: () => void) => {
      const id = window.setInterval(notify, intervalMs);
      return () => window.clearInterval(id);
    },
    [intervalMs],
  );
  // Snapshot is bucketed so React sees a stable value between ticks.
  return useSyncExternalStore(
    subscribe,
    () => Math.floor(Date.now() / intervalMs),
    () => 0,
  );
}
